import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import './LizAccounting.css';
import InputAdornment from '@material-ui/core/InputAdornment';
import { RestartAlt } from '@mui/icons-material';

function InvestorPanel({
  investor,
  profitShares,
  additions,
  subtractions,
  openManualDialog,
  onRemoveManual,
  isEditMode,
                         ericExtra = { atm: 0, owes: 0 },
                         updateEricExtra,
                         lizCashInHand = 0,
                         updateLizExtra,
                         ericSubtotal = 0,
                         onUnassignItem
}) {
  const [viewAdditionItem, setViewAdditionItem] = useState(null);
  const [viewSubtractionItem, setViewSubtractionItem] = useState(null);

  const formatAmount = (amount) =>
    Number(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const profitTotal = profitShares.reduce(
    (a, b) => a + Number(b.amount || 0),
    0
  );
  const additionsSum = additions.reduce(
    (sum, a) => sum + Number(a.amount || 0),
    0
  );
  const subtractionsSum = subtractions.reduce(
    (sum, s) => sum + Number(s.amount || 0),
    0
  );

  const additionsTotal = profitTotal + additionsSum;
  const subtractionsTotal = additionsTotal - subtractionsSum;

  const groupAdditionsForPanel = (items) => {
    const map = {};

    items.forEach((item, idx) => {
      // idx is real index in source array

      const desc = (item.description || '').trim().toLowerCase();
      const shouldMerge = desc.includes('sam') || desc.includes('donation');

      if (shouldMerge && !item.isManual) {
        // only merge non-manual items
        const key = desc.includes('sam') ? 'sam' : 'donation';
        if (!map[key])
          map[key] = {
            description: item.description,
            amount: 0,
            mergedItems: [],
            originalIndexes: [],
          };
        map[key].amount += Number(item.amount || 0);
        map[key].mergedItems.push(item);
        map[key].originalIndexes.push(idx);
      } else {
        // manual or unique items stay separate
        const uniqueKey = `unique-${idx}`;
        map[uniqueKey] = {
          description: item.description,
          locationName: item.locationName,
          amount: Number(item.amount || 0),
          mergedItems: [item],
          originalIndexes: [idx], // this keeps the correct index
        };
      }
    });

    return Object.values(map).map((item) => ({
      ...item,
      originalIndex: item.originalIndexes[0],
    }));
  };

  const mergedAdditions = groupAdditionsForPanel(additions);

  const groupSubtractionsForPanel = (items) => {
    const map = {};

    items.forEach((item, idx) => {
      const desc = (item.description || '').trim().toLowerCase();

      const isATMAdv = desc.includes('adv');

      // Check if the item is a Zelle entry and NOT manual
      const isZelle = desc.includes('zelle');

      if (isATMAdv && !item.isManual) {
        const key = 'adv';

        if (!map[key]) {
          map[key] = {
            description: `ATM ADV`,
            amount: 0,
            mergedItems: [],
            originalIndexes: [],
            isManual: false,
          };
        }

        map[key].amount += Number(item.amount || 0);
        map[key].mergedItems.push(item);
        map[key].originalIndexes.push(idx);
      }else if (isZelle && !item.isManual) {
        const key = 'zelle';

        if (!map[key]) {
          map[key] = {
            description: `Zelle`,
            amount: 0,
            mergedItems: [],
            originalIndexes: [],
            isManual: false,
          };
        }

        map[key].amount += Number(item.amount || 0);
        map[key].mergedItems.push(item);
        map[key].originalIndexes.push(idx);
      } else {
        const uniqueKey = `unique-${idx}`;
        map[uniqueKey] = {
          ...item,
          amount: Number(item.amount || 0),
          mergedItems: [item],
          originalIndexes: [idx],
        };
      }
    });

    return Object.values(map).map((item) => ({
      ...item,
      originalIndex: item.originalIndexes[0],
    }));
  };

  // Usage:
  const mergedSubtractions = groupSubtractionsForPanel(subtractions);

  // Eric extra calculations
  const SAMM = investor === 'Eric' ? 450 : 0;
  const atm = investor === 'Eric' ? Number(ericExtra.atm || 0) : 0;
  const owes = investor === 'Eric' ? Number(ericExtra.owes || 0) : 0;
  const ericSubTotal = subtractionsTotal - SAMM - atm;
  const ericTotal = ericSubTotal - owes;

  // Liz extra calculations
  const lizProfit = investor === 'Liz' ? subtractionsTotal : 0;
  const totalCash =
    investor === 'Liz' ? lizProfit + Number(ericSubtotal || 0) : 0;
  const shortOver = Number(lizCashInHand || 0) - totalCash;

  return (
    <Grid item xs={12} md={6}>
      <div className="investorPanel">
        <Typography className="investor-title">{investor}</Typography>

        {/* PROFIT BOX */}
        <Box className="section-box profit-box">
          <Typography className="section-title">Profit Share</Typography>
          {profitShares.map((p, i) => (
            <Box key={i} className="row-line">
              <Typography>{p.location}</Typography>
              <Typography>${formatAmount(p.amount)}</Typography>
            </Box>
          ))}
          <Box className="row-total">
            <Typography style={{ fontWeight: 'bold' }}>Total</Typography>
            <Typography style={{ fontWeight: 'bold' }}>
              ${formatAmount(profitTotal)}
            </Typography>
          </Box>
        </Box>

        {/* ADDITIONS BOX */}
        <Droppable droppableId={`${investor}-additions`}>
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="section-box add-box"
            >
              <Box className="section-header">
                <Typography className="section-title">Additions</Typography>
                {isEditMode && (
                  <IconButton onClick={() => openManualDialog('ADD', investor)}>
                    <AddCircleIcon color="secondary" />
                  </IconButton>
                )}
              </Box>

              {mergedAdditions.map((item, index) => {
                return (
                  <Draggable
                    key={`add-${investor}-${index}`}
                    draggableId={`add-${investor}-${index}`}
                    index={index}
                    isDragDisabled={!isEditMode}
                  >
                    {(dragProvided) => (
                      <Box
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        className="drag-item"
                        style={dragProvided.draggableProps.style}
                      >
                        <Typography
                          className="drag-text"
                          onClick={() => {
                            const desc = (item.description || '').toLowerCase();
                            const isMerged =
                              desc.includes('sam') || desc.includes('donation');
                            if (isMerged) setViewAdditionItem(item); // only open for merged items
                          }}
                        >
                          {item.description}
                          {item.locationName && ` (${item.locationName})`}
                        </Typography>

                        <div className="amount-actions">
                          <Typography>${formatAmount(item.amount)}</Typography>
                        </div>

                        <div className="amount-actions">
                          {isEditMode && item.mergedItems[0].isManual && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                onRemoveManual(
                                  investor,
                                  'ADD',
                                  item.originalIndexes[0]
                                )
                              }
                            >
                              <RemoveCircleIcon color="error" />
                            </IconButton>
                          )}

                          {isEditMode && !item.mergedItems[0].isManual && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                onUnassignItem(
                                  investor,
                                  'ADD',
                                  item.originalIndex
                                )
                              }
                            >
                              <RestartAlt color="primary" />
                            </IconButton>
                          )}
                        </div>
                      </Box>
                    )}
                  </Draggable>
                );
              })}

              {provided.placeholder}

              <Box className="row-total">
                <Typography style={{ fontWeight: 'bold' }}>Subtotal</Typography>
                <Typography style={{ fontWeight: 'bold' }}>
                  ${formatAmount(additionsTotal)}
                </Typography>
              </Box>
            </Box>
          )}
        </Droppable>

        {/* SUB BOX */}
        <Droppable droppableId={`${investor}-subtractions`}>
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="section-box sub-box"
            >
              <Box className="section-header">
                <Typography className="section-title">Subtractions</Typography>
                {isEditMode && (
                  <IconButton onClick={() => openManualDialog('SUB', investor)}>
                    <AddCircleIcon color="secondary" />
                  </IconButton>
                )}
              </Box>

              {mergedSubtractions.map((item, index) => {
                return (
                  <Draggable
                    key={`sub-${investor}-${index}`}
                    draggableId={`sub-${investor}-${index}`}
                    index={index}
                    isDragDisabled={!isEditMode}
                  >
                    {(dragProvided) => (
                      <Box
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        className="drag-item"
                        style={dragProvided.draggableProps.style}
                      >
                        <Typography
                          className="drag-text"
                          onClick={() => {
                            const desc = (item.description || '').toLowerCase();
                            // Only open view/details if it's a merged Zelle group
                            const isMerged =
                              desc.includes('zelle') || desc.includes('atm adv');
                            if (isMerged) setViewSubtractionItem(item);
                          }}
                        >
                          {item.description}
                          {item.locationName && ` (${item.locationName})`}
                        </Typography>

                        <div className="amount-actions">
                          <Typography>${formatAmount(item.amount)}</Typography>
                        </div>

                        <div className="amount-actions">
                          {/* If it's a manual entry (like a direct expense) */}
                          {isEditMode && item.mergedItems[0].isManual && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                onRemoveManual(
                                  investor,
                                  'SUB',
                                  item.originalIndexes[0]
                                )
                              }
                            >
                              <RemoveCircleIcon color="error" />
                            </IconButton>
                          )}

                          {/* If it's an auto-generated entry (like the Zelle transfer) */}
                          {isEditMode && !item.mergedItems[0].isManual && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                onUnassignItem(
                                  investor,
                                  'SUB',
                                  item.originalIndex
                                )
                              }
                            >
                              <RestartAlt color="primary" />
                            </IconButton>
                          )}
                        </div>
                      </Box>
                    )}
                  </Draggable>
                );
              })}


              {provided.placeholder}

              <Box className="row-total">
                <Typography style={{ fontWeight: 'bold' }}>Subtotal</Typography>
                <Typography style={{ fontWeight: 'bold' }}>
                  {' '}
                  ${formatAmount(subtractionsTotal)}{' '}
                </Typography>
              </Box>

              {/* === Extra Rows === */}
              {investor === 'Eric' && (
                <Box className="extra-rows">
                  {/* SAMM */}
                  <Box className="row-line">
                    <Typography>SAMM</Typography>
                    <Typography>${formatAmount(SAMM)}</Typography>
                  </Box>

                  {/* ATM Used from Profit */}
                  <Box className="row-line">
                    <Typography>ATM Used from Profit</Typography>
                    {isEditMode ? (
                      <TextField
                        size="small"
                        variant="outlined"
                        type="number"
                        value={ericExtra.atm === 0 ? '' : ericExtra.atm}
                        onChange={(e) =>
                          updateEricExtra({
                            ...ericExtra,
                            atm: e.target.value, // keep as string
                          })
                        }
                        style={{ width: 150 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                          inputProps: {
                            style: { textAlign: 'right' },
                          },
                        }}
                      />
                    ) : (
                      <Typography>${formatAmount(atm)}</Typography>
                    )}
                  </Box>

                  {/* Bold separator before subtotal */}
                  <Box className="row-total">
                    <Typography>Eric Profit</Typography>
                    <Typography>${formatAmount(ericSubTotal)}</Typography>
                  </Box>

                  {/* Eric owes from ADV ATM */}
                  <Box className="row-line">
                    <Typography>Eric owes me from ADV ATM</Typography>
                    {isEditMode ? (
                      <TextField
                        size="small"
                        variant="outlined"
                        type="number"
                        value={ericExtra.owes === 0 ? '' : ericExtra.owes}
                        onChange={(e) =>
                          updateEricExtra({
                            ...ericExtra,
                            owes: e.target.value, // keep as string
                          })
                        }
                        style={{ width: 150 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                          inputProps: {
                            style: { textAlign: 'right' },
                          },
                        }}
                      />
                    ) : (
                      <Typography>${formatAmount(owes)}</Typography>
                    )}
                  </Box>

                  {/* Bold separator before total */}
                  <Box className="row-total">
                    <Typography style={{ fontWeight: 'bold' }}>
                      Total
                    </Typography>
                    <Typography
                    style={{
                        fontWeight: 'bold',
                        color:
                          ericTotal < 0
                            ? 'red'
                            : ericTotal > 0
                            ? 'green'
                            : 'inherit'
                      }}
                    >
                      ${formatAmount(ericTotal)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* === Liz Extra Rows === */}
              {investor === 'Liz' && (
                <Box className="extra-rows">
                  <Box className="row-line">
                    <Typography>Liz Profit</Typography>
                    <Typography>${formatAmount(lizProfit || 0)}</Typography>
                  </Box>

                  {/* Eric Profit */}
                  <Box className="row-line">
                    <Typography>Eric Profit</Typography>
                    <Typography>${formatAmount(ericSubtotal)}</Typography>
                  </Box>

                  {/* Total Cash */}
                  <Box className="row-line">
                    <Typography>Total Cash</Typography>
                    <Typography>${formatAmount(totalCash)}</Typography>
                  </Box>

                  {/* Cash In Hand */}
                  <Box className="row-line">
                    <Typography>Cash In Hand</Typography>
                    {isEditMode ? (
                      <TextField
                        size="small"
                        variant="outlined"
                        type="number"
                        value={lizCashInHand === 0 ? '' : lizCashInHand}
                        onChange={(e) =>
                          updateLizExtra(
                            e.target.value === '' ? '' : Number(e.target.value)
                          )
                        }
                        style={{ width: 150 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                          inputProps: {
                            style: { textAlign: 'right' },
                          },
                        }}
                      />
                    ) : (
                      <Typography>${formatAmount(lizCashInHand)}</Typography>
                    )}
                  </Box>

                  {/* Bold separator before Short/Over */}
                  <Box className="row-total">
                    <Typography style={{ fontWeight: 'bold' }}>
                      Short/Over
                    </Typography>
                    <Typography
                        style={{
                          fontWeight: 'bold',
                          color:
                            shortOver < 0
                              ? 'red'
                              : shortOver > 0
                              ? 'green'
                              : 'inherit'
                        }}
                    >
                      ${formatAmount(shortOver)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Droppable>

        {/* DIALOG FOR VIEWING ADDITION */}
        <Dialog
          open={!!viewSubtractionItem}
          onClose={() => setViewSubtractionItem(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {viewSubtractionItem?.description} Breakdown
          </DialogTitle>
          <DialogContent dividers>
            {viewSubtractionItem?.mergedItems?.map((item, i) => (
              <Box
                key={i}
                sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}
                className="row-line"
              >
                <Typography variant="body1">
                  {item.locationName || 'System Entry'}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  ${formatAmount(item.amount)}
                </Typography>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setViewSubtractionItem(null)}
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!viewAdditionItem}
          onClose={() => setViewAdditionItem(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{viewAdditionItem?.description} Breakdown</DialogTitle>
          <DialogContent>
            {viewAdditionItem?.mergedItems.map((item, i) => (
              <Box key={i} className="row-line">
                <Typography>{item.locationName || 'N/A'}</Typography>
                <Typography>${formatAmount(item.amount)}</Typography>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewAdditionItem(null)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Grid>
  );
}

export default InvestorPanel;
